import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/RichTextEditor";
import { EmailTemplatesMenu, firstNameOf, insertTemplateHtml } from "@/components/EmailTemplatesMenu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Mail, Loader2, Sparkles, Plus, RefreshCw, Paperclip, ExternalLink,
  CheckCircle2, Bot, Inbox as InboxIcon, History, Send, Search,
  Settings2, Zap, Check, X, ListChecks, ScrollText, CornerUpRight,
  FolderCheck, AlertTriangle, Slack as SlackIcon, Reply as ReplyIcon, ReplyAll, UserPlus, UserCheck, Ban, Banknote, MailCheck, PenSquare,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { useSlack } from "@/contexts/SlackContext";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { ScheduleEditor, ScheduleBadge } from "@/components/AutopilotSchedule";
import MondayMirror from "@/pages/applications/MondayMirror";
import { DailyReportPopover } from "@/pages/applications/DailyReportPopover";
import { AckSettings, AckDialog } from "@/pages/applications/InboxAcknowledgement";
import { Landmark, ShieldCheck, Table2, UserRound } from "lucide-react";
import CreditSenseHub from "@/pages/applications/creditsense/CreditSenseHub";
import { InboxShell } from "@/pages/inbox/InboxShell";
import SumsubHub from "@/pages/applications/sumsub/SumsubHub";
import { deptInboxApi, sumsubHub, creditSense } from "@/lib/api";
import { NeedsReviewTable } from "@/pages/applications/NeedsReviewTable";
import { InboxAuditLog } from "@/pages/applications/InboxAuditLog";

const REPLY_KIND_LABEL = { credit_query: "credit query (needs an underwriter)", acceptance: "offer / conditions accepted", documents: "documents or information sent", holding: "holding reply — nothing new", other: "other" };
import {
  INTENT_TONE, fmtKb, fmtUkDate,
  StatusSelect, AttachmentPreviewDialog, AttachmentActionIcon, autopilotPill, AutopilotPill, DuplicateTag,
} from "@/pages/applications/inboxShared";
import { EmailList, InboxSearch, InboxTabs } from "@/pages/inbox/EmailList";
import { ForwardDialog } from "@/pages/inbox/ForwardDialog";
import { MoveToMenu } from "@/pages/inbox/MoveToMenu";
import { BulkBar } from "@/pages/inbox/BulkBar";
import { AssigneeFilter, applyAssigneeFilter } from "@/pages/inbox/AssigneeFilter";
import { RecipientInput } from "@/pages/inbox/RecipientInput";
import { AppComposeDialog } from "@/pages/applications/AppComposeDialog";
import { AttachmentsStrip, EmailBodyFrame } from "@/pages/inbox/ReadingPaneParts";
import { useHiddenEmails, usePullCadence } from "@/pages/inbox/useInboxPolling";
import {
  appInboxList, appInboxGetEmail, appInboxClassify, appInboxExtract, appInboxExtractStatus,
  appInboxCreateApp, appInboxGetSettings, appInboxSetSettings,
  appInboxHistory, appInboxPrepUpdate, appInboxPostUpdate,
  appInboxQueue, appInboxQueueApprove, appInboxQueueReject, appInboxRunAutopilot,
  appInboxGetAttachments, appInboxAttachmentBlob, appInboxForward, appInboxMoveDone,
  appInboxStatusOptions, appInboxSetAppStatus,
  appInboxReply, appInboxAssign, appInboxBrokers, appInboxAttachSO,
  appInboxSearchApps, appInboxAutopilotStatus, appInboxLoanDiskBorrowers,
  setAppInboxRegion, appInboxAusDiagnose,
  appInboxApi, appInboxFolders, appInboxMove, appInboxSearch,
} from "@/lib/api";
import { AttachmentsBox } from "@/components/mail/AttachmentsBox";
import { buildAttachmentsPayload, hasInlineImage, mergeItems, splitInlineImages, uploadItem } from "@/components/mail/attachments";
import { appFileSource, appScope } from "@/components/mail/fileSources";
import { useIsPhone } from "@/hooks/useIsPhone";
import { PhoneAppsInboxView } from "@/pages/applications/phone/PhoneAppsInboxView";
import { useSearchParams } from "react-router-dom";


export default function ApplicationsInbox() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [triaging, setTriaging] = useState(false);
  const [wizardFor, setWizardFor] = useState(null);   // email row
  const [postFor, setPostFor] = useState(null);       // email row → post-update dialog
  const [forwardFor, setForwardFor] = useState(null); // email row → forward dialog
  const [mode, setMode] = useState("manual");
  const [autoTriage, setAutoTriage] = useState(true);
  const [autoConfidence, setAutoConfidence] = useState(90);
  const [minKb, setMinKb] = useState(30);
  const [otherFwd, setOtherFwd] = useState("");
  const [tab, setTab] = useState("inbox");
  const [view, setView] = useState("inbox");   // inbox | mirror | sumsub
  // Dean (9 Oct): the signed-in person's own work mailbox sits beside applications@ in the header - same shell as Collections / Legal, AI triage on, Autopilot never.
  // Dean (12 Oct): on the AUS hub that mailbox is the person's @simplyfunded.com.au address (AU tenant), not their UK one.
  const { user } = useAuth();
  const { currentRegion } = useRegion();
  const isAus = currentRegion === "AUS";
  setAppInboxRegion(currentRegion);   // module flag read by every appInbox* call
  const [sumsubNew, setSumsubNew] = useState(0); // red pill on the Sumsub tab — unseen approved / failed / on-hold checks
  useEffect(() => {
    let alive = true;
    const tick = () => sumsubHub.attention(currentRegion).then((r) => alive && setSumsubNew(r.count || 0)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [currentRegion]);
  const [creditSenseNew, setCreditSenseNew] = useState(0); // red pill on the AU Credit Sense tab — completed bank-statement reports nobody has opened
  useEffect(() => {
    if (!isAus) { setCreditSenseNew(0); return undefined; }
    let alive = true;
    const tick = () => creditSense.attention().then((r) => alive && setCreditSenseNew(r.count || 0)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isAus]);

  const [mailbox, setMailbox] = useState("shared");   // shared | mine
  const [mine, setMine] = useState({ available: false, mailbox: "", reason: "" });
  useEffect(() => {
    let alive = true;
    setMine({ available: false, mailbox: "", reason: "" });
    deptInboxApi("mine", isAus ? "AUS" : "UK").available()
      .then(r => alive && setMine({ available: !!r.available, mailbox: r.mailbox || "", reason: r.reason || "" }))
      .catch(() => alive && setMine({ available: false, mailbox: "", reason: "Couldn't check your mailbox" }));
    return () => { alive = false; };
  }, [isAus]);
  useEffect(() => { if (mailbox === "mine" && !mine.available) setMailbox("shared"); }, [mailbox, mine.available]);
  const [queue, setQueue] = useState({ items: [], counts: {} });
  const [running, setRunning] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [cutoff, setCutoff] = useState("16:30");
  const [dupDays, setDupDays] = useState(14);
  const [emailDupDays, setEmailDupDays] = useState(30);
  const [condAlertEmail, setCondAlertEmail] = useState("");
  const [folder, setFolder] = useState("inbox");
  const folderRef = useRef("inbox");
  const [triageIntel, setTriageIntel] = useState("");
  const [statusIntel, setStatusIntel] = useState("");
  const [intelSaving, setIntelSaving] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [ackSettings, setAckSettings] = useState(null);
  const [newAppsOnly, setNewAppsOnly] = useState(false);
  const [scope, setScope] = useState({ post_update: false, mark_done: false, ob_connected: false, forward_email: false, cancellation: false });
  const [autoConfidencePost, setAutoConfidencePost] = useState(90);
  const [salesHold, setSalesHold] = useState(true);   // Dean 9 Oct 2026 — credit-approved deals' emails stay with Sales
  const [schedule, setSchedule] = useState({
    schedule_enabled: true, schedule_days: [0, 1, 2, 3, 4],
    schedule_start: "08:00", schedule_end: "18:00",
  });
  const [replyFor, setReplyFor] = useState(null);   // { email, match }
  const [pipe, setPipe] = useState(null);
  // Dean (12 Oct 2026): work solely from the portal — folders, whole-mailbox search, filters, multi-select, New email
  const hubApi = useMemo(() => appInboxApi(isAus ? "AUS" : "UK"), [isAus]);
  const [folders, setFolders] = useState([]);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState(null);       // {q, emails} when a whole-mailbox search is showing
  const [searching, setSearching] = useState(false);
  const [who, setWho] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [checked, setChecked] = useState(() => new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const isPhone = useIsPhone();
  const [deepStub, setDeepStub] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const openParam = searchParams.get("open");
  useEffect(() => {
    if (!openParam || !data) return;
    const hit = (data.emails || []).find((e) => e.id === openParam || e.internet_message_id === openParam);
    if (hit) setSelectedId(hit.id); else { setDeepStub({ id: openParam, subject: "Opening email…", from_email: "" }); setSelectedId(openParam); }
    setSearchParams((p) => { p.delete("open"); return p; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openParam, data]);
  const loadFolders = useCallback((force = false) => appInboxFolders(force).then(r => setFolders(r.folders || [])).catch(() => {}), []);

  const { stripHidden, hideEmail } = useHiddenEmails(setData);

  const fetchEmails = useCallback(async (silent = false, folderOverride = null) => {
    try {
      if (!silent) setLoading(true);
      const f = folderOverride || folderRef.current;
      const r = await appInboxList({ limit: f === "inbox" ? 500 : 100, folder: f });
      setData(f === "inbox" ? stripHidden(r) : r);
      setErr(null);
      setLastSync(new Date());
    } catch (e) {
      if (!silent) setErr(e.response?.data?.detail || e.message);
    } finally { if (!silent) setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchQueue = useCallback(async () => {
    try { setQueue(await appInboxQueue("all", 200)); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    setAppInboxRegion(currentRegion);
    setData(null); setSelectedId(null); setErr(null);
    setQueue({ items: [], counts: {} });
    setFolders([]); setSearch(null); setQuery(""); setWho("all"); setChecked(new Set());
    setFolder("inbox"); folderRef.current = "inbox"; setTab("inbox");
    fetchEmails(); fetchQueue(); loadFolders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEmails, fetchQueue, currentRegion]);
  useEffect(() => {
    appInboxGetSettings().then(r => {
      setAckSettings({ ack_enabled: r.ack_enabled, ack_templates: r.ack_templates });
      setMode(r.mode);
      setAutoTriage(r.auto_triage !== false);
      setNewAppsOnly(!!r.autopilot_new_apps_only);
      if (r.autopilot_scope) setScope(r.autopilot_scope);
      setSalesHold(r.sales_handles_credit_approved !== false);
      if (r.auto_confidence) setAutoConfidence(r.auto_confidence);
      if (r.auto_confidence_post) setAutoConfidencePost(r.auto_confidence_post);
      if (r.min_attachment_kb != null) setMinKb(r.min_attachment_kb);
      setOtherFwd(r.other_forward_to || "");
      if (r.app_date_cutoff) setCutoff(r.app_date_cutoff);
      if (r.duplicate_decline_days != null) setDupDays(r.duplicate_decline_days);
      if (r.email_dup_days != null) setEmailDupDays(r.email_dup_days);
      if (r.conditions_alert_email != null) setCondAlertEmail(r.conditions_alert_email);
      setTriageIntel(r.triage_intel || "");
      setStatusIntel(r.status_intel || "");
      if (r.schedule_days) setSchedule({
        schedule_enabled: r.schedule_enabled !== false, schedule_days: r.schedule_days,
        schedule_start: r.schedule_start, schedule_end: r.schedule_end,
      });
    }).catch(() => {});
  }, [currentRegion]);
  // Live pipeline heartbeat — shows what triage/autopilot is working on
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const r = await appInboxAutopilotStatus();
        if (!alive) return;
        setPipe(r);
        // settings are global — keep every user's UI in sync with the server
        if (r.mode) setMode(r.mode);
        if (r.auto_triage != null) setAutoTriage(r.auto_triage !== false);
        if (r.autopilot_new_apps_only != null) setNewAppsOnly(!!r.autopilot_new_apps_only);
        if (r.autopilot_scope) setScope(r.autopilot_scope);
        if (r.auto_confidence_post) setAutoConfidencePost(r.auto_confidence_post);
        if (r.sales_handles_credit_approved != null) setSalesHold(r.sales_handles_credit_approved !== false);
        if (r.schedule_days) setSchedule({
          schedule_enabled: r.schedule_enabled !== false, schedule_days: r.schedule_days,
          schedule_start: r.schedule_start, schedule_end: r.schedule_end,
        });
      } catch { /* noop */ }
    };
    poll();
    const t = setInterval(() => { if (document.visibilityState === "visible") poll(); }, 12000);
    return () => { alive = false; clearInterval(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRegion]);
  // Auto-pull: fixed 30s cadence anchored to the wall clock — the cycle keeps
  // ticking regardless of page/mounts and a manual PullMail never restarts it.
  const countdown = usePullCadence(() => { fetchEmails(true); fetchQueue(); if (folderRef.current !== "inbox") loadFolders(true); });

  const runSearch = async () => {
    const q = query.trim();
    if (q.length < 2) { toast.info("Type at least 2 characters to search"); return; }
    setSearching(true);
    try {
      const r = await appInboxSearch(q);
      setSearch({ q, emails: r.emails || [] }); setSelectedId(null); setChecked(new Set());
      if (!(r.emails || []).length) toast.info(`Nothing found for “${q}” in ${r.mailbox || "the mailbox"}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setSearching(false); }
  };
  const clearSearch = () => { setQuery(""); setSearch(null); setSelectedId(null); setChecked(new Set()); };
  const toggleChecked = (id) => setChecked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const dropEmail = (id) => { hideEmail(id); setSearch(prev => prev ? { ...prev, emails: prev.emails.filter(e => e.id !== id) } : prev); setSelectedId(null); };
  const afterBulk = async (r) => {
    if (r.action !== "assign" && r.action !== "mark_read") [...checked].forEach(id => dropEmail(id));
    setChecked(new Set());
    await Promise.all([fetchEmails(), fetchQueue()]); loadFolders(true);
    if (search) runSearch();
  };
  const moveTo = async (email, f) => {
    await appInboxMove(email.id, f.kind === "inbox" ? "inbox" : f.kind === "done" ? "done" : f.id);
    dropEmail(email.id);
    await fetchEmails(); loadFolders(true);
  };
  const openFolder = (id) => {
    setTab(id === "inbox" || id === "done" || id === "sent" ? id : "folder");
    setFolder(id); folderRef.current = id;
    setSelectedId(null); setSearch(null); setQuery(""); setChecked(new Set());
    fetchEmails(false, id);
  };

  const changeMode = async (m) => {
    const prev = mode;
    setMode(m);
    try {
      await appInboxSetSettings({ mode: m });
      toast.success(
        m === "manual" ? "Manual mode — nothing happens without you"
        : `Autopilot on — emails at ≥${autoConfidence}% confidence process automatically`);
      if (m === "auto") setTab("queue");
    } catch (e) {
      setMode(prev);
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const saveSetting = async (patch) => {
    try {
      const r = await appInboxSetSettings(patch);
      setAckSettings({ ack_enabled: r.ack_enabled, ack_templates: r.ack_templates });
      setAutoConfidence(r.auto_confidence);
      if (r.auto_confidence_post) setAutoConfidencePost(r.auto_confidence_post);
      setMinKb(r.min_attachment_kb);
      setOtherFwd(r.other_forward_to || "");
      setNewAppsOnly(!!r.autopilot_new_apps_only);
      if (r.autopilot_scope) setScope(r.autopilot_scope);
      setSalesHold(r.sales_handles_credit_approved !== false);
      if (r.app_date_cutoff) setCutoff(r.app_date_cutoff);
      if (r.duplicate_decline_days != null) setDupDays(r.duplicate_decline_days);
      if (r.email_dup_days != null) setEmailDupDays(r.email_dup_days);
      if (r.conditions_alert_email != null) setCondAlertEmail(r.conditions_alert_email);
      if (r.schedule_days) setSchedule({
        schedule_enabled: r.schedule_enabled !== false, schedule_days: r.schedule_days,
        schedule_start: r.schedule_start, schedule_end: r.schedule_end,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const saveIntel = async () => {
    setIntelSaving(true);
    try {
      await appInboxSetSettings({ triage_intel: triageIntel, status_intel: statusIntel });
      toast.success("Triage intelligence saved — the AI follows it from the next email onwards");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setIntelSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await appInboxRunAutopilot();
      if (r.already_running) toast.info("Autopilot is already scanning — the queue updates live");
      else toast.success("Scan started — working through every new email; the queue updates as items complete");
      await Promise.all([fetchQueue(), fetchEmails(true)]);
      setTimeout(() => { fetchQueue(); fetchEmails(true); }, 10000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setRunning(false); }
  };

  const moveDone = async (email) => {
    try {
      await appInboxMoveDone(email.id);
      toast.success("Email moved to the Done folder");
      hideEmail(email.id);
      setSelectedId(null);
      await Promise.all([fetchEmails(), fetchQueue()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const allEmails = useMemo(() => {
    const rows = (search ? search.emails : data?.emails) || [];
    const outbound = !search && (folder === "sent" || folder === "drafts" || folders.find(f => f.id === folder)?.kind === "sent");
    // Sent Items / Drafts: show who the mail went TO rather than applications@ itself
    return outbound ? rows.map(e => ({ ...e, from_name: `To: ${e.to_name || e.to_email || "(no recipient)"}${e.to_count > 1 ? ` +${e.to_count - 1}` : ""}`, from_email: e.to_email || e.from_email })) : rows;
  }, [data, search, folder, folders]);
  const emails = useMemo(() => applyAssigneeFilter(allEmails, who, user, unreadOnly), [allEmails, who, user, unreadOnly]);
  const inboxTotal = folder === "inbox" && data?.total != null ? data.total : (data?.emails || []).length;
  const pipeActive = !!(pipe && (pipe.running || pipe.triage_running || pipe.autopilot_running));
  // Badge = exactly what the Needs review list shows (Paul: badge said 12, list had 8 —
  // the DB-wide count included proposals outside the fetched window).
  const pendingCount = (queue.items || []).filter(i => ["proposed", "failed"].includes(i.status)).length;
  // newest Autopilot queue row per email — drives the "waiting for automation / needs you" pill on each row
  const queueByMsg = useMemo(() => {
    const m = new Map();
    for (const q of [...(queue.items || [])].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))) {
      if (q.message_id) m.set(q.message_id, q);
      if (q.internet_message_id) m.set(q.internet_message_id, q);
    }
    return m;
  }, [queue]);
  const pillCtx = {
    mode, autoTriage, pipeActive,
    withinHours: pipe?.schedule ? pipe.schedule.within_hours !== false : true,
    nextChangeLabel: pipe?.schedule?.next_change_label || "",
  };
  const selected = useMemo(() => {
    const found = allEmails.find(e => e.id === selectedId);
    if (found) return found;
    const q = (queue.items || []).find(i => i.message_id === selectedId
      && ["proposed", "failed"].includes(i.status));
    if (q) {
      return {
        id: q.message_id, subject: q.subject, from_email: q.from_email,
        from_name: q.from_email, received: q.received,
        has_attachments: (q.attachments || []).length > 0,
        triage: { intent: q.intent, confidence: q.confidence, summary: q.summary },
      };
    }
    if (deepStub && deepStub.id === selectedId) return deepStub;
    return null;
  }, [allEmails, queue, selectedId, deepStub]);

  const mergeTriage = (id, triage) => {
    setData(prev => prev ? { ...prev, emails: prev.emails.map(e => e.id === id ? { ...e, triage } : e) } : prev);
    setSearch(prev => prev ? { ...prev, emails: prev.emails.map(e => e.id === id ? { ...e, triage } : e) } : prev);
  };

  const mergeAssign = (id, assigned) => {
    setData(prev => prev ? { ...prev, emails: prev.emails.map(e => e.id === id ? { ...e, assigned } : e) } : prev);
    setSearch(prev => prev ? { ...prev, emails: prev.emails.map(e => e.id === id ? { ...e, assigned } : e) } : prev);
  };

  const triageAll = async () => {
    const pending = emails.filter(e => !e.triage).slice(0, 20);
    if (!pending.length) { toast.info("Everything visible is already triaged"); return; }
    setTriaging(true);
    let done = 0;
    const pool = [...pending];
    const worker = async () => {
      while (pool.length) {
        const e = pool.shift();
        try {
          const r = await appInboxClassify(e.id);
          mergeTriage(e.id, { intent: r.triage.intent, confidence: r.triage.confidence, summary: r.triage.summary,
            matched_sf: r.triage.matched_sf, matched_status: r.triage.matched_status,
            intent_corrected_from: r.triage.intent_corrected_from });
          done++;
        } catch { /* keep going */ }
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    setTriaging(false);
    toast.success(`Triaged ${done} email${done === 1 ? "" : "s"}`);
  };

  const extraFolders = folders.filter(f => f.kind !== "inbox" && f.kind !== "done" && f.kind !== "sent");
  const activeExtra = extraFolders.find(f => f.id === folder) || null;
  const doneRow = folders.find(f => f.kind === "done");
  const sentRow = folders.find(f => f.kind === "sent");
  const tabsBar = (
    <InboxTabs
      tabs={[
        { id: "inbox", label: `Inbox (${inboxTotal})`, icon: InboxIcon },
        { id: "done", label: "Done", icon: CheckCircle2, count: doneRow?.total },
        { id: "sent", label: "Sent", icon: Send, count: sentRow?.total, title: "Everything sent from the applications mailbox" },
        { id: "queue", label: "Needs review", icon: ListChecks, badge: pendingCount },
        { id: "audit", label: "Audit log", icon: ScrollText, title: "What the portal and Autopilot did to each email — readable, filterable, exportable" },
      ]}
      active={search ? "__search" : activeExtra ? "__folders" : tab}
      onChange={(id) => { if (id === "queue") { setTab("queue"); setSearch(null); setChecked(new Set()); fetchQueue(); } else if (id === "audit") { setTab("audit"); setSearch(null); setChecked(new Set()); } else openFolder(id); }}
      folders={extraFolders} activeFolder={search ? null : activeExtra}
    />
  );
  const moveDoneFolder = async (email) => { await moveDone(email); loadFolders(true); };

  const renderBadges = (e) => (
    <>
      {e.source === "partner_api" && (
        <Badge variant="outline" className="text-[9px] bg-cyan-50 text-cyan-800 border-cyan-300" title={`Submitted through the Partner API · ${e.partner_ref}`} data-testid={`inbox-api-badge-${e.internet_message_id || e.id}`}>
          API · {e.partner_ref}
        </Badge>
      )}
      {e.triage && (
        <Badge variant="outline" className={cn("text-[9px]", INTENT_TONE[e.triage.intent] || INTENT_TONE.Other)}>
          {e.triage.intent} · {e.triage.confidence}%
        </Badge>
      )}
      {folder === "inbox" && !search && (
        <AutopilotPill pill={autopilotPill(e, queueByMsg.get(e.id) || queueByMsg.get(e.internet_message_id), pillCtx)}
          testId={`autopilot-pill-${e.internet_message_id || e.id}`} />
      )}
      {!e.triage && pipeActive && (
        <span className="inline-flex items-center gap-1.5" data-testid={`triage-working-${e.internet_message_id || e.id}`}>
          <span className="relative w-14 h-1 rounded-full bg-violet-100 overflow-hidden inline-block">
            <span className="absolute inset-y-0 w-1/3 bg-violet-500 rounded-full animate-working-bar" />
          </span>
          <span className="text-[9px] font-medium text-violet-700">
            {pipe?.current && e.subject && pipe.current === e.subject.slice(0, 120) ? "Working… AI reading now" : "Working…"}
          </span>
        </span>
      )}
      {e.triage?.matched_sf && (
        <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-300"
          title={e.triage.intent_corrected_from
            ? `Intent corrected from "${e.triage.intent_corrected_from}" — this app is at ${e.triage.matched_status} on Monday`
            : "Matched live application · current Monday status"}
          data-testid={`matched-status-badge-${e.internet_message_id || e.id}`}>
          {e.triage.matched_sf} · {e.triage.matched_status}
          {e.triage.intent_corrected_from ? " ✓corrected" : ""}
        </Badge>
      )}
      {e.assigned && (
        <Badge variant="outline" className="text-[9px] bg-sky-50 text-sky-700 border-sky-300 gap-0.5" data-testid={`assigned-badge-${e.internet_message_id || e.id}`}>
          <UserCheck className="w-2.5 h-2.5" />{(e.assigned.name || "").split(" ")[0]}
        </Badge>
      )}
      {e.app && (
        <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-300 gap-0.5">
          <CheckCircle2 className="w-2.5 h-2.5" />{e.app.sf_number}
        </Badge>
      )}
      {(e.updates_posted || []).map((u, i) => (
        <Badge key={i} variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-300 gap-0.5">
          <Send className="w-2.5 h-2.5" />{u.sf || "posted"}
        </Badge>
      ))}
    </>
  );

  const readingPane = selected ? (
    <ReadingPane
      email={selected}
      me={user}
      pipeActive={pipeActive}
      folders={folders}
      currentFolder={search ? selected.folder_id : folder}
      onMove={(f) => moveTo(selected, f)}
      onTriaged={(t) => mergeTriage(selected.id, t)}
      onCreateApp={() => setWizardFor(selected)}
      onPostUpdate={() => setPostFor(selected)}
      onForward={() => setForwardFor(selected)}
      onMoveDone={() => moveDoneFolder(selected)}
      onReply={(match) => setReplyFor({ email: selected, match })}
      onAssigned={(a) => mergeAssign(selected.id, a)}
      onHandled={async () => { dropEmail(selected?.id); await Promise.all([fetchEmails(), fetchQueue()]); }}
    />
  ) : null;

  const mineShell = (
    <InboxShell key={`mine-${isAus ? "AUS" : "UK"}`} dept="mine" title="My mailbox" icon={UserRound} testid="my-mailbox-shell" embedded hideAutopilot
      region={isAus ? "AUS" : "UK"}
      caseLink={(c) => `${isAus ? "/aus/collections/ageing" : "/collections/ageing"}/${c.case_id}`}
      subtitle="your own work email — AI triage on, Autopilot never; reply, log to files and work from the portal" />
  );
  const phoneCtx = isPhone ? {
    user, isAus, data, loading, err, view, setView, sumsubNew, creditSenseNew, mailbox, setMailbox, mine, mineShell,
    mirror: <MondayMirror key={currentRegion} />, sumsub: <SumsubHub key={currentRegion} onAttentionChange={setSumsubNew} />,
    creditsense: isAus ? <CreditSenseHub onAttentionChange={setCreditSenseNew} /> : null,
    tab, folder, openFolder, extraFolders, doneRow, inboxTotal, pendingCount, allEmails, emails, who, setWho, unreadOnly, setUnreadOnly,
    query, setQuery, runSearch, clearSearch, search, searching, checked, setChecked, toggleChecked, afterBulk, hubApi, folders,
    selected, setSelectedId, renderBadges, readingPane, hideEmail, dropEmail, mergeAssign,
    openReply: (email, match, replyAll) => setReplyFor({ email, match, replyAll }),
    openForward: (email) => setForwardFor(email),
    refresh: () => { fetchEmails(); fetchQueue(); loadFolders(true); },
    mode, changeMode, autoTriage, running, runNow, queue, setSettingsOpen, setComposeOpen, triageAll, triaging,
    onQueueChanged: async () => { await Promise.all([fetchQueue(), fetchEmails()]); },
    setAutoTriageSetting: async (v) => {
      setAutoTriage(v);
      try { await appInboxSetSettings({ auto_triage: v }); toast.success(v ? "Auto-triage on" : "Auto-triage off"); }
      catch (e) { setAutoTriage(!v); toast.error(e.response?.data?.detail || e.message); }
    },
  } : null;

  return (
    <div className={isPhone ? "p-3" : "p-3 sm:p-6 space-y-4"} data-testid="applications-inbox-page">
      {isPhone && <PhoneAppsInboxView ctx={phoneCtx} />}
      {!isPhone && (<>
      <div className="flex items-center gap-1 border-b overflow-x-auto" data-testid="apps-view-tabs">
        {[
          { id: "inbox", label: "Inbox / Triage", icon: InboxIcon },
          { id: "mirror", label: "Monday Mirror", icon: Table2 },
          { id: "sumsub", label: "Sumsub", icon: ShieldCheck, badge: sumsubNew },
          ...(isAus ? [{ id: "creditsense", label: "Credit Sense", icon: Landmark, badge: creditSenseNew }] : []),
        ].map(v => (
          <button key={v.id} onClick={() => setView(v.id)}
            className={cn("flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 sm:px-4 py-2 text-sm transition border-b-2 -mb-px",
              view === v.id ? "border-blue-600 font-semibold text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800")}
            data-testid={`apps-view-tab-${v.id}`}>
            <v.icon className="w-4 h-4" />{v.label}
            {v.badge > 0 && <span className="ml-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white" data-testid={`apps-view-tab-${v.id}-badge`}>{v.badge}</span>}
          </button>
        ))}
      </div>
      {view === "mirror" && <MondayMirror key={currentRegion} />}
      {view === "sumsub" && <SumsubHub key={currentRegion} onAttentionChange={setSumsubNew} />}
      {view === "creditsense" && isAus && <CreditSenseHub onAttentionChange={setCreditSenseNew} />}
      <div className={view !== "inbox" ? "hidden" : "space-y-4"}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <InboxIcon className="w-5 h-5 text-blue-600" />
          <h1 className="text-xl font-bold">Application Hub</h1>
          <Badge className={cn("text-[10px] font-bold", isAus ? "bg-emerald-600 hover:bg-emerald-600" : "bg-blue-600 hover:bg-blue-600")} data-testid="inbox-region-badge">
            {isAus ? "AUS" : "UK"}
          </Badge>
          {/* Dean (9 Oct): the shared applications@ mailbox and your own mailbox sit side by side here — fewer tabs on the top row */}
          <div className="flex items-center gap-0.5 rounded-md border bg-white p-0.5" data-testid="apps-mailbox-switch">
            <button type="button" onClick={() => setMailbox("shared")}
              className={cn("rounded px-2 py-0.5 font-mono text-[10px] transition", mailbox === "shared" ? "bg-slate-900 text-white font-semibold" : "text-slate-600 hover:bg-slate-100")}
              data-testid="apps-mailbox-shared">
              {data?.mailbox || (isAus ? "applications@…com.au" : "applications@")}
            </button>
            <span className="text-slate-300">|</span>
            <button type="button" onClick={() => mine.available && setMailbox("mine")} disabled={!mine.available}
              title={mine.available ? `Your own work mailbox — ${mine.mailbox}` : (mine.reason || "Your sign-in isn't a SimplyFunded mailbox")}
              className={cn("flex items-center gap-1 rounded px-2 py-0.5 text-[10px] transition", mailbox === "mine" ? "bg-slate-900 text-white font-semibold" : mine.available ? "text-slate-600 hover:bg-slate-100" : "text-slate-300 cursor-not-allowed")}
              data-testid="apps-mailbox-mine">
              <UserRound className="w-3 h-3" />My mailbox{mine.mailbox && mine.available ? <span className="font-mono text-[10px] opacity-70">({mine.mailbox})</span> : null}
            </button>
          </div>
          {mailbox === "shared" && <Badge variant="outline" className="text-[10px]">{emails.length} emails</Badge>}
          {mailbox === "shared" && <PipelineActivity pipe={pipe} />}
        </div>
        {mailbox === "shared" && (
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-8 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setComposeOpen(true)} data-testid="compose-new-email-btn"
            title={`Write a new email from ${data?.mailbox || (isAus ? "applications@simplyfunded.com.au" : "applications@simplyfunded.co.uk")}`}>
            <PenSquare className="w-3.5 h-3.5 mr-1" />New email
          </Button>
          <DailyReportPopover actor={user?.name || user?.email || "Ops"} />
          {isAus && <AusSetupCheck />}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 border rounded-md px-2 py-1 bg-white cursor-pointer" data-testid="auto-triage-toggle">
            <Sparkles className="w-3 h-3 text-violet-600" />
            Auto-triage
            <Switch checked={autoTriage} onCheckedChange={async (v) => {
              setAutoTriage(v);
              try {
                await appInboxSetSettings({ auto_triage: v });
                toast.success(v ? "Auto-triage on — new emails get badges within ~2 min" : "Auto-triage off");
              } catch (e) { setAutoTriage(!v); toast.error(e.response?.data?.detail || e.message); }
            }} className="scale-75" data-testid="auto-triage-switch" />
          </label>
          <div className="flex items-center gap-0.5 border rounded-md p-0.5 bg-white" data-testid="autopilot-toggle">
            {[{ id: "manual", label: "Manual" }, { id: "auto", label: "Autopilot" }].map(m => (
              <button
                key={m.id}
                onClick={() => m.id !== mode && changeMode(m.id)}
                className={cn("px-3 py-1 text-[11px] rounded transition",
                  mode === m.id
                    ? (m.id === "auto" ? "bg-green-700 text-white font-semibold" : "bg-slate-900 text-white font-semibold")
                    : "text-slate-600 hover:bg-slate-100")}
                data-testid={`autopilot-mode-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {mode === "auto" && <ScheduleBadge schedule={pipe?.schedule} testid="inbox-ap-schedule-badge" />}
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setSettingsOpen(true)} data-testid="autopilot-settings-btn">
            <Settings2 className="w-3.5 h-3.5" />
            <span className="text-[11px]">Filters &amp; intel</span>
            <span className="text-[11px] font-mono">{autoConfidence}%</span>
          </Button>
          <Button size="sm" variant="outline" onClick={triageAll} disabled={triaging || loading} data-testid="triage-all-btn">
            {triaging ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1 text-violet-600" />}
            AI triage
          </Button>
          <Button size="sm" variant="outline"
            onClick={() => { fetchEmails(); fetchQueue(); if (mode === "auto" && !running) runNow(); }}
            disabled={loading} data-testid="inbox-refresh-btn"
            title={`${mode === "auto" ? "Pull mail + run the Autopilot scan now. " : "Pull the latest mail from the inbox. "}Auto-pulls every 30s — a manual pull doesn't restart the timer${lastSync ? ` · last pull ${lastSync.toLocaleTimeString("en-GB")}` : ""}`}>
            {loading || running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1 text-[11px]">PullMail</span>
          </Button>
          <span className="text-[10px] text-muted-foreground font-mono tabular-nums" data-testid="next-pull-countdown"
            title={lastSync ? `Last pull ${lastSync.toLocaleTimeString("en-GB")}` : "Auto-pulls every 30 seconds"}>
            next pull {countdown}s
          </span>
        </div>
        )}
      </div>

      {mailbox === "mine" && mine.available && (
        <div className="flex flex-col h-[calc(100vh-13rem)] min-h-0" data-testid="apps-my-mailbox-wrap">
          {mineShell}
        </div>
      )}
      <div className={mailbox === "mine" ? "hidden" : "space-y-4"}>
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>}

      {tab === "audit" ? (
        <Card data-testid="inbox-audit-card">
          {tabsBar}
          <InboxAuditLog />
        </Card>
      ) : tab === "queue" ? (
        <Card data-testid="needs-review-card">
          {tabsBar}
          <NeedsReviewTable
            queue={queue}
            mode={mode}
            running={running}
            onRun={runNow}
            selectedId={selectedId}
            onSelect={(mid) => setSelectedId(mid)}
            readingPane={readingPane}
            onChanged={async () => { await Promise.all([fetchQueue(), fetchEmails()]); }}
          />
        </Card>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        {/* Email list */}
        <Card className="lg:col-span-2 flex flex-col">
          {tabsBar}
          <InboxSearch value={query} onChange={setQuery} onSearch={runSearch} onClear={clearSearch} searching={searching} active={!!search} resultCount={search?.emails.length || 0} />
          <AssigneeFilter emails={allEmails} me={user} value={who} onChange={setWho} unreadOnly={unreadOnly} onUnreadToggle={setUnreadOnly} />
          {checked.size > 0 && (
            <BulkBar count={checked.size} api={hubApi} me={user} ids={[...checked]} folders={folders.filter(f => ["inbox", "done", "folder"].includes(f.kind))}
              currentFolder={search ? "__search" : folder} onDone={afterBulk} onClear={() => setChecked(new Set())} />
          )}
          <EmailList emails={emails} loading={loading && !search} hasData={!!data || !!search} selectedId={selectedId} onSelect={setSelectedId}
            loadingText="Loading applications inbox…"
            emptyText={search ? `Nothing found for “${search.q}”.` : (who !== "all" || unreadOnly) && allEmails.length ? "No emails match this filter." : tab === "done" ? "The Done folder is empty." : tab === "sent" ? "Nothing has been sent from this mailbox yet." : activeExtra ? "This folder is empty." : "Inbox is empty."}
            renderBadges={renderBadges} selectable checked={checked} onToggle={toggleChecked} />
        </Card>

        {/* Reading pane */}
        <Card className="lg:col-span-3">
          {readingPane || (
            <CardContent className="p-10 text-center text-sm text-muted-foreground">
              <Mail className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Select an email to read it
            </CardContent>
          )}
        </Card>
      </div>
      )}
      </div>
      </div>
      </>)}

      {wizardFor && (
        <CreateAppWizard
          email={wizardFor}
          onClose={() => setWizardFor(null)}
          onCreated={async () => { hideEmail(wizardFor?.id); setWizardFor(null); setSelectedId(null); await Promise.all([fetchEmails(), fetchQueue()]); }}
        />
      )}
      {postFor && (
        <PostUpdateDialog
          email={postFor}
          onClose={() => setPostFor(null)}
          onPosted={async () => { hideEmail(postFor?.id); setPostFor(null); setSelectedId(null); await Promise.all([fetchEmails(), fetchQueue()]); }}
        />
      )}
      {forwardFor && (
        <ForwardDialog
          email={forwardFor}
          forward={appInboxForward}
          onClose={() => setForwardFor(null)}
          onForwarded={async (movedDone) => {
            if (movedDone) { hideEmail(forwardFor?.id); setSelectedId(null); }
            setForwardFor(null);
            await Promise.all([fetchEmails(), fetchQueue()]);
          }}
        />
      )}
      {settingsOpen && (
        <Dialog open onOpenChange={(v) => !v && setSettingsOpen(false)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="autopilot-settings-dialog">
            <DialogHeader>
              <DialogTitle className="text-base flex items-center gap-2">
                <Settings2 className="w-4 h-4" />Filters &amp; Intelligence
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div className="col-span-2 border rounded-md px-3 py-2 bg-green-50/60 border-green-200" data-testid="autopilot-scope-row">
                  <div className="text-xs font-semibold">What Autopilot may do on its own</div>
                  <p className="text-[10px] text-muted-foreground mt-0.5 mb-2">
                    New applications always run when Autopilot is on. Everything below is drafted either way; ticked = executed
                    at or above the replies threshold, unticked = waits in Needs review for a human.
                    {newAppsOnly && <strong className="ml-1 text-amber-700">Currently: new applications only.</strong>}
                  </p>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    {[
                      ["post_update", "Post replies onto the matched deal", "A reply on an information-requested deal moves it to UW - IR; every other status is left as it is — never backwards, never past offer."],
                      ["mark_done", "Move junk / bounces to Done", "Spam, promos and auto-replies with no deal."],
                      ["ob_connected", "Mark Open Banking connected", "Sets the OB column and posts the note on the matched deal."],
                      ["forward_email", "Forward invoices & broker intros", "To accounts@ / the broker-intro list."],
                      ["cancellation", "Cancellations → Offer Rejected", "Only when the AI reads a clear rejected status; otherwise it still waits for a human."],
                    ].map(([k, label, help]) => (
                      <div key={k} className="flex items-start justify-between gap-3" data-testid={`autopilot-scope-${k}-row`}>
                        <div>
                          <div className="text-[11px] font-medium">{label}</div>
                          <p className="text-[10px] text-muted-foreground">{help}</p>
                        </div>
                        <Switch checked={!!scope[k]}
                          onCheckedChange={(v) => { setScope(s => ({ ...s, [k]: v })); saveSetting({ autopilot_scope: { [k]: v } }); }}
                          data-testid={`autopilot-scope-${k}-switch`} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="col-span-2 flex items-center justify-between gap-4 border rounded-md px-3 py-2 bg-fuchsia-50/60 border-fuchsia-200" data-testid="sales-hold-row">
                  <div>
                    <div className="text-xs font-semibold">Sales handle credit-approved deals</div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      When on, emails matched to a deal at <strong>Raise Doc's</strong>, <strong>Doc's Out</strong> or <strong>Ready To Fund</strong> — or sitting in the
                      "Ready for Doc's raised" group — are never auto-posted or moved to Done. They stay in the inbox and show in Needs review
                      tagged "Sales to action", so the sales team see them and process them themselves. {isAus ? "Australia" : "UK"} inbox only.
                    </p>
                  </div>
                  <Switch checked={salesHold}
                    onCheckedChange={(v) => { setSalesHold(v); saveSetting({ sales_handles_credit_approved: v }); }}
                    data-testid="sales-hold-switch" />
                </div>
                <div className="col-span-2 border rounded-md px-3 py-2 bg-slate-50/60" data-testid="inbox-ap-schedule-row">
                  <ScheduleEditor
                    value={schedule}
                    onChange={(patch) => { setSchedule(p => ({ ...p, ...patch })); saveSetting(patch); }}
                    testidPrefix="inbox-ap-schedule"
                    tzLabel={isAus ? "Sydney time (AEST)" : "UK time"}
                  />
                </div>
                <div className="col-span-2">
                  <div className="flex justify-between text-xs font-medium mb-2">
                    <span>Confidence threshold — new applications</span>
                    <span className="font-mono" data-testid="confidence-value">{autoConfidence}%</span>
                  </div>
                  <Slider value={[autoConfidence]} min={50} max={100} step={1}
                    onValueChange={([v]) => setAutoConfidence(v)}
                    onValueCommit={([v]) => saveSetting({ auto_confidence: v })}
                    data-testid="confidence-slider" />
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    New-app emails triaged below this always wait for your approval. Raise it on busy days for extra caution.
                  </p>
                </div>
                <div className="col-span-2">
                  <div className="flex justify-between text-xs font-medium mb-2">
                    <span>Confidence threshold — replies / Done / OB / forwards</span>
                    <span className="font-mono" data-testid="confidence-post-value">{autoConfidencePost}%</span>
                  </div>
                  <Slider value={[autoConfidencePost]} min={50} max={100} step={1}
                    onValueChange={([v]) => setAutoConfidencePost(v)}
                    onValueCommit={([v]) => saveSetting({ auto_confidence_post: v })}
                    data-testid="confidence-post-slider" />
                  <p className="text-[10px] text-muted-foreground mt-1.5">
                    Posting a reply onto a deal Autopilot has matched is low-risk — in the 30-day audit it never picked the wrong deal. 90% is the recommended line.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Minimum attachment size (KB)</div>
                  <Input type="number" min={0} max={500} className="h-7 text-xs w-24" value={minKb}
                    onChange={(e) => setMinKb(Number(e.target.value))}
                    onBlur={() => saveSetting({ min_attachment_kb: Math.max(0, Math.min(500, minKb || 0)) })}
                    data-testid="min-attachment-kb-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Files under this size (signature logos etc.) are unticked by default and skipped by Autopilot.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Application date cutoff ({isAus ? "Sydney time (AEST)" : "UK time"})</div>
                  <Input type="time" className="h-7 text-xs w-28" value={cutoff}
                    onChange={(e) => setCutoff(e.target.value)}
                    onBlur={() => cutoff && saveSetting({ app_date_cutoff: cutoff })}
                    data-testid="app-date-cutoff-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    New apps from emails received after this time are dated the next working day (weekends &amp; {isAus ? "NSW public holidays" : "UK bank holidays"} skipped).
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Duplicate decline window (days)</div>
                  <Input type="number" min={0} max={90} className="h-7 text-xs w-24" value={dupDays}
                    onChange={(e) => setDupDays(Number(e.target.value))}
                    onBlur={() => saveSetting({ duplicate_decline_days: Math.max(0, Math.min(90, dupDays || 0)) })}
                    data-testid="duplicate-window-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    A new app for a company declined within this window is flagged as a duplicate instead of being created. Live apps are always flagged. 0 = only live apps count.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Same-customer-email window (days)</div>
                  <Input type="number" min={0} max={90} className="h-7 text-xs w-24" value={emailDupDays}
                    onChange={(e) => setEmailDupDays(Number(e.target.value))}
                    onBlur={() => saveSetting({ email_dup_days: Math.max(0, Math.min(90, emailDupDays || 0)) })}
                    data-testid="email-dup-window-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    A new app whose customer email matches any application within this window is treated as the same client (even under a different entity or broker) — autopilot won't auto-create it and the wizard shows a warning. 0 = off.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Offer-conditions alert — email to</div>
                  <Input type="email" placeholder={isAus ? "e.g. nick@simplyfunded.com.au (empty = off)" : "paul.richards@simplyfunded.co.uk"} className="h-7 text-xs" value={condAlertEmail}
                    onChange={(e) => setCondAlertEmail(e.target.value)}
                    onBlur={() => saveSetting({ conditions_alert_email: condAlertEmail.trim() })}
                    data-testid="conditions-alert-email-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    When a reply returns offer conditions, this person gets a "CONDITION RECEIVED" email. Leave empty to switch the alert off for this region.
                  </p>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Unmatched "Other" emails — forward to</div>
                  <Input type="email" placeholder="name@simplyfunded.co.uk" className="h-7 text-xs" value={otherFwd}
                    onChange={(e) => setOtherFwd(e.target.value)}
                    onBlur={() => saveSetting({ other_forward_to: otherFwd.trim() })}
                    data-testid="other-forward-to-input" />
                  <p className="text-[10px] text-muted-foreground mt-1">
                    "Other" emails that don't match any application get a one-click forward proposal to this address. Leave empty to keep them in Needs review.
                  </p>
                </div>
              </div>
              <AckSettings settings={ackSettings} me={user} onSaved={(r) => setAckSettings({ ack_enabled: r.ack_enabled, ack_templates: r.ack_templates })} />
              <div className="border-t pt-4 space-y-3" data-testid="triage-intel-section">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  <div>
                    <div className="text-sm font-semibold">Triage intelligence</div>
                    <p className="text-[10px] text-muted-foreground">
                      Teach the AI in plain English. These rules are injected into its prompts as authoritative operator rules — it follows them from the very next email, no code changes needed.
                    </p>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Classification rules <span className="font-normal text-muted-foreground">(what intent an email gets)</span></div>
                  <Textarea rows={4} className="text-xs font-mono" value={triageIntel}
                    onChange={(e) => setTriageIntel(e.target.value)}
                    placeholder={'e.g.\n- Emails from broker X mentioning "conditions" are always Final Checks.\n- Anything from accounts@acme.co.uk is an Invoice.'}
                    data-testid="triage-intel-input" />
                </div>
                <div>
                  <div className="text-xs font-medium mb-1">Status rules <span className="font-normal text-muted-foreground">(what next status gets suggested)</span></div>
                  <Textarea rows={4} className="text-xs font-mono" value={statusIntel}
                    onChange={(e) => setStatusIntel(e.target.value)}
                    placeholder={'e.g.\n- If the reply includes signed contracts, suggest "Doc\'s Out".\n- Never suggest Declined without a human review.'}
                    data-testid="status-intel-input" />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-muted-foreground">
                    The pipeline itself (Application → UW → IR/Offer branches → Final Checks, never backwards) is already built in — use these for the exceptions and broker quirks.
                  </p>
                  <Button size="sm" onClick={saveIntel} disabled={intelSaving} data-testid="save-intel-btn">
                    {intelSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
                    Save intelligence
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {replyFor && (
        <ReplyDialog
          email={replyFor.email}
          matchMode={replyFor.match}
          initialReplyAll={!!replyFor.replyAll}
          me={user}
          region={currentRegion}
          api={hubApi}
          fromLabel={data?.mailbox || ""}
          onClose={() => setReplyFor(null)}
          onSent={async (movedDone) => {
            if (movedDone) dropEmail(replyFor?.email?.id);
            setReplyFor(null);
            await Promise.all([fetchEmails(), fetchQueue()]); loadFolders(true);
          }}
        />
      )}
      {composeOpen && (
        <AppComposeDialog api={hubApi} me={user} fromLabel={data?.mailbox || (isAus ? "applications@simplyfunded.com.au" : "applications@simplyfunded.co.uk")}
          onClose={() => setComposeOpen(false)}
          onSent={() => { setComposeOpen(false); loadFolders(true); }} />
      )}
    </div>
  );
}

function AusSetupCheck() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await appInboxAusDiagnose();
      const part = (label, p) => `${p?.ok ? "OK" : "FAIL"} — ${label}${p?.ok ? (p.board ? ` (${p.board})` : "") : `: ${p?.error || "not configured"}`}`;
      const lines = [part("AU mailbox (M365)", r.m365), part("AUS Airtable", r.airtable), part("Monday board", r.monday)];
      if (r.next_sfa) lines.push(r.next_sfa.ok ? `OK — next number: SFA-${r.next_sfa.value}` : `FAIL — SFA lookup: ${r.next_sfa.error}`);
      (r.ok ? toast.success : toast.warning)(lines.join("\n"), { duration: 15000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };
  return (
    <Button size="sm" variant="outline" className="gap-1 text-emerald-700 border-emerald-300" onClick={run}
      disabled={busy} data-testid="aus-setup-check-btn"
      title="Checks the AU M365 tenant, AUS Airtable access and the AUS Monday board">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
      <span className="text-[11px]">AUS setup check</span>
    </Button>
  );
}

function AttachSODialog({ email, atts, me, onClose }) {
  const prefillSf = email.triage?.matched_sf
    || (email.subject || "").match(/SF-?\d+(?:\/\d+)?/i)?.[0]?.toUpperCase().replace(/^SF(\d)/, "SF-$1")
    || email.updates_posted?.[0]?.sf || "";
  const [attId, setAttId] = useState(atts[0]?.id || "");
  const [sf, setSf] = useState(prefillSf);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    try {
      setBusy(true);
      const r = await appInboxAttachSO(email.id, {
        attachment_id: attId, app_ref: sf.trim(),
        actor: me?.email || me?.name || "Ops",
      });
      toast.success(r.queued
        ? `Attached to ${r.sf || sf} — pushes to LoanDisk once the loan is validated`
        : `Standing order attached to ${r.sf || sf} and uploaded to LoanDisk`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Attach failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="attach-so-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2">
            <Banknote className="w-4 h-4 text-emerald-600" />
            Attach Standing Order to a deal
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Which file is the standing order?</div>
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {atts.map((a) => (
                <label key={a.id} className={cn("flex items-center gap-2 border rounded px-2 py-1.5 text-xs cursor-pointer",
                  attId === a.id ? "border-emerald-400 bg-emerald-50" : "hover:bg-slate-50")}>
                  <input type="radio" name="so-att" checked={attId === a.id} onChange={() => setAttId(a.id)}
                    data-testid={`attach-so-att-${a.id}`} />
                  <Paperclip className="w-3 h-3 text-slate-400 shrink-0" />
                  <span className="truncate flex-1">{a.name}</span>
                  {a.pasted && <span className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1">pasted</span>}
                  <span className="text-muted-foreground shrink-0">{fmtKb(a.size)}</span>
                </label>
              ))}
              {!atts.length && (
                <div className="text-xs text-muted-foreground border rounded p-2">No attachments found on this email.</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Deal (SF number)</div>
            <Input value={sf} onChange={(e) => setSf(e.target.value)} placeholder="e.g. SF-29507"
              className="h-8 text-sm" data-testid="attach-so-sf-input" />
            <p className="text-[10px] text-muted-foreground mt-1">
              Goes onto the deal's funding workflow as its Standing Order document and pushes to the LoanDisk loan.
            </p>
          </div>
          <Button className="w-full" size="sm" onClick={submit} disabled={busy || !attId || !sf.trim()}
            data-testid="attach-so-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Banknote className="w-3.5 h-3.5 mr-1.5" />}
            Attach to deal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReadingPane({ email, me, pipeActive, folders = [], currentFolder, onMove, onTriaged, onCreateApp, onPostUpdate, onForward, onMoveDone, onReply, onAssigned, onHandled }) {
  const { currentRegion } = useRegion();
  const [ackOpen, setAckOpen] = useState(false);
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [hist, setHist] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [atts, setAtts] = useState([]);
  const [previewAtt, setPreviewAtt] = useState(null);
  const [soOpen, setSoOpen] = useState(false);
  const [movingDone, setMovingDone] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const selRef = useRef("");
  const slack = useSlack();

  const snapToSlack = () => {
    if (!slack) return;
    let quote = "";
    try {
      const iframe = document.querySelector('[data-testid="reading-pane-body"]');
      quote = (iframe?.contentWindow?.getSelection?.()?.toString() || "").trim();
    } catch { /* sandboxed */ }
    if (!quote) quote = selRef.current;   // last selection made inside the email
    if (!quote) {
      const tmp = document.createElement("div");
      tmp.innerHTML = msg?.body?.content || "";
      quote = (tmp.textContent || "").replace(/\s+/g, " ").trim().slice(0, 600);
    }
    const t = email.triage;
    const lines = [
      ":email: *Email snap — Application Hub*",
      `*From:* ${email.from_name || ""} <${email.from_email || ""}>`,
      `*Subject:* ${email.subject || "(no subject)"}`,
      email.received ? `*Received:* ${new Date(email.received).toLocaleString("en-GB")}` : null,
      t?.intent ? `*AI triage:* ${t.intent} (${t.confidence}%)${t.summary ? ` — ${t.summary}` : ""}` : null,
      "",
      ...quote.split("\n").filter(Boolean).map(l => `> ${l}`),
      "",
      "Any advice on how to handle this one?",
    ].filter(l => l !== null);
    slack.expandWithDraft(lines.join("\n"));
  };

  useEffect(() => {
    let alive = true;
    setMsg(null);
    setHist(null);
    setHistOpen(false);
    setAtts([]);
    selRef.current = "";
    (async () => {
      try {
        setLoading(true);
        const r = await appInboxGetEmail(email.id);
        if (alive) setMsg(r.message);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    if (email.has_attachments) {
      appInboxGetAttachments(email.id).then(r => { if (alive) setAtts(r.attachments || []); }).catch(() => {});
    }
    if (email.triage && (email.triage.company_name || email.triage.sf_no)) {
      appInboxHistory(email.id).then(r => { if (alive) setHist(r); }).catch(() => {});
    }
    return () => { alive = false; };
  }, [email.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const classify = async () => {
    setClassifying(true);
    try {
      const r = await appInboxClassify(email.id, true);
      onTriaged({ intent: r.triage.intent, confidence: r.triage.confidence, summary: r.triage.summary,
        matched_sf: r.triage.matched_sf, matched_status: r.triage.matched_status,
        intent_corrected_from: r.triage.intent_corrected_from });
    } catch (e) {
      toast.error("Triage failed: " + (e.response?.data?.detail || e.message));
    } finally { setClassifying(false); }
  };

  const assignToMe = async () => {
    setAssigning(true);
    try {
      const r = await appInboxAssign(email.id, me?.name || me?.email || "Me", me?.email || "");
      onAssigned(r.assigned);
      toast.success("Assigned to you");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setAssigning(false); }
  };

  const unassign = async () => {
    setAssigning(true);
    try {
      await appInboxAssign(email.id, "");
      onAssigned(null);
      toast.success("Unassigned");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setAssigning(false); }
  };

  const intent = email.triage?.intent;
  const suggestsNew = ["New App", "Top Up"].includes(intent);
  const suggestsUpdate = ["Info Requested", "Final Checks", "Chase/Status Update", "Open Banking", "Cancellation/Withdrawn"].includes(intent);

  return (
    <>
      <CardHeader className="pb-2 border-b">
        <div className="min-w-0">
          <CardTitle className="text-sm truncate" data-testid="reading-pane-subject">{email.subject || "(no subject)"}</CardTitle>
          <div className="text-xs text-muted-foreground mt-0.5">
            {email.from_name} &lt;{email.from_email}&gt; · {email.received ? new Date(email.received).toLocaleString("en-GB") : ""}
          </div>
          {!email.triage && pipeActive && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1.5" data-testid="reading-pane-working">
              <Bot className="w-3 h-3 shrink-0" />
              <span className="font-medium shrink-0">Working… AI triage in progress</span>
              <span className="relative flex-1 h-1 rounded-full bg-violet-200 overflow-hidden">
                <span className="absolute inset-y-0 w-1/3 bg-violet-600 rounded-full animate-working-bar" />
              </span>
            </div>
          )}
          {email.triage?.summary && (
            <div className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1 mt-1.5 flex items-start gap-1.5">
              <Bot className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                {email.triage.summary}
                {email.triage.matched_sf && (
                  <span className="ml-1.5 font-semibold text-indigo-800" data-testid="reading-pane-matched-status">
                    · {email.triage.matched_sf} is at "{email.triage.matched_status}"
                    {email.triage.matched_broker ? <span data-testid="reading-pane-matched-broker"> · via {email.triage.matched_broker}</span> : ""}
                    {email.triage.intent_corrected_from ? ` (intent corrected from ${email.triage.intent_corrected_from})` : ""}
                  </span>
                )}
              </span>
            </div>
          )}
          {hist && hist.history?.length > 0 && (
            <div className="mt-1.5" data-testid="history-strip">
              <button
                onClick={() => setHistOpen(o => !o)}
                className={cn("text-[10px] px-2 py-0.5 rounded border inline-flex items-center gap-1",
                  hist.duplicates?.length ? "bg-rose-50 text-rose-700 border-rose-300" : "bg-slate-50 text-slate-600 border-slate-200")}
                data-testid="history-toggle"
              >
                <History className="w-2.5 h-2.5" />
                {hist.history.length} previous app{hist.history.length === 1 ? "" : "s"}
                {hist.duplicates?.length ? ` · ${hist.duplicates.length} LIVE` : ""}
              </button>
              {histOpen && (
                <div className="border rounded mt-1 divide-y max-h-28 overflow-y-auto bg-white">
                  {hist.history.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 text-[10px]">
                      <span className="font-mono font-semibold">{h.sf}</span>
                      <span className="truncate flex-1">{h.name}</span>
                      {h.broker && (
                        <span className="text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-px max-w-[140px] truncate shrink-0"
                          title={`Broker: ${h.broker}`} data-testid={`history-broker-${i}`}>
                          {h.broker}
                        </span>
                      )}
                      <Badge variant="outline" className={cn("text-[9px]",
                        h.is_live ? "bg-green-50 text-green-700 border-green-300" : "")}>
                        {h.status || "—"}
                      </Badge>
                      <span className="text-muted-foreground shrink-0">{fmtUkDate(h.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      {/* Action prompts — the manual walkthrough */}
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b bg-slate-50" data-testid="email-action-bar">
        {email.app ? (
          <a href={email.app.item_url} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-green-700 border-green-300" data-testid="view-created-app-btn">
              <CheckCircle2 className="w-3 h-3 mr-1" />{email.app.sf_number}<ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </a>
        ) : (
          <Button size="sm" variant={suggestsNew ? "default" : "outline"} className="h-7 text-[11px]" onClick={onCreateApp} data-testid="create-new-app-btn">
            <Plus className="w-3 h-3 mr-1" />New app
          </Button>
        )}
        <Button size="sm" variant={suggestsUpdate ? "default" : "outline"} className="h-7 text-[11px]" onClick={onPostUpdate} data-testid="post-to-existing-btn">
          <Send className="w-3 h-3 mr-1" />Add to existing
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onReply(false)} data-testid="reply-btn"
          title="Reply to the sender (general enquiry — nothing is logged)">
          <ReplyIcon className="w-3 h-3 mr-1" />Reply
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] text-blue-700 border-blue-300" onClick={() => onReply(true)} data-testid="match-reply-btn"
          title="Reply to the sender AND log the reply as an update on the matched application">
          <ReplyIcon className="w-3 h-3 mr-1" />Match &amp; Reply
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] text-sky-700 border-sky-300" onClick={() => setAckOpen(true)} data-testid="send-ack-btn"
          title="Send the acknowledgement template for this email's intent to the sender (editable before sending)">
          <MailCheck className="w-3 h-3 mr-1" />Send acknowledgement
        </Button>
        {ackOpen && <AckDialog email={email} intent={email?.triage?.intent} me={me} onClose={() => setAckOpen(false)} onSent={() => setHist(null)} />}
        {currentRegion !== "AUS" && <Button size="sm" variant="outline" className="h-7 text-[11px] text-emerald-700 border-emerald-300" onClick={() => setSoOpen(true)}
          disabled={!atts.length} data-testid="attach-so-btn"
          title="Attach a standing order (file or pasted image) from this email straight onto the matched deal's funding workflow + LoanDisk loan">
          <Banknote className="w-3 h-3 mr-1" />Attach SO
        </Button>}
        {(hist?.duplicates || []).length > 0 && !email.app && (
          <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
            onClick={onCreateApp} data-testid="mark-duplicate-btn"
            title={`This company already has ${hist.duplicates[0]?.sf || "a live application"} — the app is still created for broker tracking, flagged "Duplicate Application" for UW and logged on the existing deal`}>
            <Ban className="w-3 h-3 mr-1" />
            Mark duplicate
          </Button>
        )}
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onForward} data-testid="forward-email-btn">
          <CornerUpRight className="w-3 h-3 mr-1" />Forward
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-600" disabled={movingDone}
          onClick={async () => { setMovingDone(true); try { await onMoveDone(); } finally { setMovingDone(false); } }}
          data-testid="move-done-btn">
          {movingDone ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FolderCheck className="w-3 h-3 mr-1" />}
          Move to done
        </Button>
        {onMove && <MoveToMenu folders={folders.filter(f => ["inbox", "done", "folder"].includes(f.kind))} current={currentFolder} onMove={onMove} />}
        <div className="ml-auto flex items-center gap-1">
          {email.assigned ? (
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-sky-700 border-sky-300 bg-sky-50 hover:bg-sky-100"
              disabled={assigning} onClick={unassign} title="Click to unassign" data-testid="assigned-chip-btn">
              {assigning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserCheck className="w-3 h-3 mr-1" />}
              {email.assigned.name}
            </Button>
          ) : (
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-600"
              disabled={assigning} onClick={assignToMe} title="Tag this email as yours so other processors don't double-handle it"
              data-testid="assign-to-me-btn">
              {assigning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <UserPlus className="w-3 h-3 mr-1" />}
              Assign to me
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-7 text-[11px] text-[#611f69] border-[#611f69]/30 hover:bg-[#611f69]/5"
            onClick={snapToSlack} disabled={!slack} title="Send a snap of this email to a Slack channel (select text first to quote just that part)"
            data-testid="slack-snap-btn">
            <SlackIcon className="w-3 h-3 mr-1" />Slack
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500" onClick={classify} disabled={classifying} data-testid="classify-email-btn">
            {classifying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-violet-600" />}
            {email.triage ? "Re-triage" : "AI triage"}
          </Button>
        </div>
      </div>

      {/* Attachments strip */}
      <AttachmentsStrip atts={atts} messageId={email.id} onPreview={setPreviewAtt} />

      <EmailBodyFrame html={msg?.body?.content} loading={loading} onSelection={(s) => { selRef.current = s; }} />
      {previewAtt && (
        <AttachmentPreviewDialog messageId={email.id} att={previewAtt} onClose={() => setPreviewAtt(null)} />
      )}
      {soOpen && (
        <AttachSODialog email={email} atts={atts} me={me} onClose={() => setSoOpen(false)} />
      )}
    </>
  );
}

function CreateAppWizard({ email, onClose, onCreated }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [fields, setFields] = useState({});
  const [sfNumber, setSfNumber] = useState(null);
  const [attSel, setAttSel] = useState(new Set());
  const [postUpdate, setPostUpdate] = useState(true);
  const [sendAck, setSendAck] = useState(false);
  useEffect(() => {
    appInboxGetSettings().then((r) => {
      const t = r.ack_templates?.[email?.triage?.intent === "Top Up" ? "Top Up" : "New App"];
      setSendAck(!!(r.ack_enabled && t?.enabled));
    }).catch(() => {});
  }, [email?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [creating, setCreating] = useState(false);
  const [previewAtt, setPreviewAtt] = useState(null);
  const [broker, setBroker] = useState(null);       // { item_id, name }
  const [brokerAuto, setBrokerAuto] = useState(false);
  const [isTopup, setIsTopup] = useState(false);
  const [ldCustomer, setLdCustomer] = useState(null); // picked LoanDisk borrower

  useEffect(() => {
    let alive = true;
    const applyResult = (r) => {
      setPrefill(r);
      setFields(r.fields);
      setSfNumber(r.sf_number);
      setAttSel(new Set((r.attachments || []).filter(a => a.selected).map(a => a.id)));
      if (r.broker_match) { setBroker(r.broker_match); setBrokerAuto(true); }
      if ((r.previously_funded || []).length > 0 &&
          (r.fields?.broker_or_direct === "Broker" || r.broker_match)) {
        setIsTopup(true);
      }
    };
    (async () => {
      try {
        // Extraction runs server-side in the background (attachment OCR on
        // heavy emails can take minutes — well past the proxy timeout).
        const started = await appInboxExtract(email.id);
        if (!alive) return;
        if (started.status === "done") { applyResult(started); setLoading(false); return; }
        const deadline = Date.now() + 6 * 60 * 1000;
        while (alive && Date.now() < deadline) {
          await new Promise((res) => setTimeout(res, 2500));
          if (!alive) return;
          const st = await appInboxExtractStatus(email.id);
          if (st.status === "done") { applyResult(st); setLoading(false); return; }
          if (st.status === "error") throw new Error(st.error || "Extraction failed");
          if (st.status === "none") throw new Error("Extraction was interrupted — close and try again");
        }
        if (alive) throw new Error("Extraction timed out — close the dialog and try again");
      } catch (e) {
        if (alive) { setErr(e.response?.data?.detail || e.message); setLoading(false); }
      }
    })();
    return () => { alive = false; };
  }, [email.id]);

  const set = (k, v) => setFields(prev => ({ ...prev, [k]: v }));

  const dupHits = (prefill?.duplicate_hits?.length ? prefill.duplicate_hits : (prefill?.duplicates || [])).slice(0, 5);
  const bestDup = dupHits.find(d => d.item_id && d.why === "live") || dupHits.find(d => d.item_id) || dupHits[0] || null;

  const pickLdCustomer = (b) => {
    if (!b) { setLdCustomer(null); return; }
    setLdCustomer(b);
    setFields(prev => ({
      ...prev,
      company_name: b.business_name || prev.company_name,
      clean_name: (b.business_name || "").replace(/\s+(ltd|limited)\.?\s*$/i, "").trim() || prev.clean_name,
      contact_name: b.name || prev.contact_name,
      customer_email: b.email || prev.customer_email,
    }));
    setIsTopup(true);
    toast.success(`Pulled in ${b.business_name || b.name} from LoanDisk — marked as Top-Up (TU)`);
  };

  const submit = async () => {
    setCreating(true);
    try {
      const ldNote = ldCustomer
        ? `Existing LoanDisk customer: ${ldCustomer.business_name || ldCustomer.name} (borrower #${ldCustomer.borrower_id})`
        : "";
      const r = await appInboxCreateApp(email.id, {
        sf_number: sfNumber,
        company_name: fields.company_name,
        clean_name: fields.clean_name,
        company_number: fields.company_number || "",
        contact_name: fields.contact_name,
        broker_or_direct: fields.broker_or_direct,
        contact_email: prefill?.sender_email,
        customer_email: fields.customer_email,
        key_information: [ldNote, fields.key_information].filter(Boolean).join("\n"),
        broker_item_id: broker?.item_id || "",
        broker_name: broker?.name || "",
        attachment_ids: [...attSel],
        post_email_update: postUpdate,
        is_topup: isTopup,
        send_ack: sendAck,
        duplicate_of: bestDup,
        duplicates: dupHits,
        director_name: fields.director_name || "",
        amount_requested: fields.amount_requested || "",
        postcode: fields.postcode || "",
        address: fields.address || "",
        attachment_names: (prefill?.attachments || []).filter(a => attSel.has(a.id)).map(a => a.name || ""),
      });
      (r.warnings || []).forEach(w => toast.warning(w));
      if (r.ack?.sent) toast.success(`Acknowledgement sent to ${r.ack.to}`);
      else if (sendAck && r.ack?.reason) toast.warning(`Acknowledgement not sent: ${r.ack.reason}`);
      if (r.duplicate_flag) toast.warning(`Flagged for UW: ${r.duplicate_flag}`, { duration: 8000 });
      const ik = r.intake || {};
      if (ik.jurisdiction?.stop) toast.error(`Intake: ${ik.jurisdiction.reason} — moved to "${ik.moved_to_group || "Pre-screen decline - to confirm"}"`, { duration: 10000 });
      if (ik.decline_hold?.hold) toast.error(`Intake: decline hold — ${ik.decline_hold.reason}${ik.moved_to_group ? ` — moved to "${ik.moved_to_group}"` : ""}`, { duration: 10000 });
      if (ik.minimum_application && !ik.minimum_application.complete) toast.warning(`Intake: application incomplete — missing ${ik.minimum_application.missing.join(", ")}`, { duration: 10000 });
      const attNote = r.attachments_queued ? ` — ${r.attachments_queued} file${r.attachments_queued > 1 ? "s" : ""} uploading in background` : "";
      toast.success(`${r.sf_number} created on Monday${attNote}`, {
        action: { label: "Open in Monday", onClick: () => window.open(r.item_url, "_blank") },
        duration: 8000,
      });
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setCreating(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="create-app-wizard">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Plus className="w-4 h-4 text-blue-600" />
            New application {sfNumber ? <Badge variant="outline" className="font-mono">SF-{sfNumber}</Badge> : null}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground" data-testid="app-wizard-loading">
            <Bot className="w-6 h-6 mx-auto mb-2 text-violet-600 animate-pulse" />
            Claude is reading the email &amp; attachments, fetching the next SF number…
            <div className="text-[11px] text-slate-400 mt-1.5">
              Scanned documents can take a couple of minutes — leave this open, it'll fill in automatically.
            </div>
          </div>
        ) : err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="app-wizard-error">{err}</div>
        ) : (
          <div className="space-y-3">
            {prefill?.ai_error && (
              <div className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded p-2.5 flex items-start gap-2" data-testid="app-wizard-ai-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                <div>
                  <div className="font-medium">Claude couldn't read this email — you can still create the app.</div>
                  <div className="mt-0.5">{prefill.ai_error}</div>
                  <div className="mt-0.5 text-amber-800">The fields below were filled from the subject and sender only — check and complete them before creating.</div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <WizField label="Company name (as registered)" placeholder="ACME INDUSTRIES LTD"
                value={fields.company_name} onChange={(v) => set("company_name", v)} testId="app-wizard-company_name" />
              <WizField label="Clean name (no LTD)" placeholder="ACME INDUSTRIES"
                value={fields.clean_name} onChange={(v) => set("clean_name", v)} testId="app-wizard-clean_name" />
              <WizField label="Contact name" placeholder="Who submitted it"
                value={fields.contact_name} onChange={(v) => set("contact_name", v)} testId="app-wizard-contact_name" />
              <div>
                <label className="text-[11px] font-medium text-slate-600">Broker / Direct</label>
                <div className="flex gap-1 mt-0.5">
                  {["Broker", "Direct"].map(v => (
                    <button key={v} onClick={() => set("broker_or_direct", v)}
                      className={cn("px-3 py-1.5 text-xs border rounded flex-1 transition",
                        fields.broker_or_direct === v ? "bg-slate-900 text-white font-semibold" : "hover:bg-slate-50")}
                      data-testid={`app-wizard-source-${v.toLowerCase()}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
              <WizField label="Customer email (optional)" placeholder="applicant@company.co.uk"
                value={fields.customer_email} onChange={(v) => set("customer_email", v)} testId="app-wizard-customer_email" />
              <div>
                <label className="text-[11px] font-medium text-slate-600">Submitter (from email)</label>
                <Input value={prefill?.sender_email || ""} disabled className="h-8 text-xs bg-slate-50" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-medium text-slate-600">Broker (linked on the Monday broker board)</label>
                <BrokerPicker value={broker} autoMatched={brokerAuto}
                  onChange={(b) => { setBroker(b); setBrokerAuto(false); }} />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-medium text-slate-600">Existing LoanDisk customer (search to pull their details in)</label>
                <LoanDiskCustomerPicker value={ldCustomer} onChange={pickLdCustomer}
                  seedQuery={fields.company_name || fields.clean_name || ""} />
              </div>
            </div>
            <div className={cn("text-[11px] rounded p-2 space-y-1 border",
              (prefill?.previously_funded || []).length > 0 || isTopup
                ? "text-violet-900 bg-violet-50 border-violet-300"
                : "text-slate-600 bg-slate-50 border-slate-200")}
              data-testid="topup-suggestion">
              {(prefill?.previously_funded || []).length > 0 && (
                <div className="font-semibold">
                  ↻ Previously funded: {prefill.previously_funded.map(d => d.sf || d.name).filter(Boolean).slice(0, 3).join(", ")} — likely a Top-Up
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isTopup} onChange={(e) => setIsTopup(e.target.checked)} data-testid="topup-toggle" />
                Create as Top-Up — adds "(TU)" to the item name &amp; sets status "TopUp - UW"
              </label>
            </div>
            {fields.sf_existing && (
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-2" data-testid="existing-sf-warning">
                ⚠ This email references <b>{fields.sf_existing}</b> — it may relate to an existing application rather than a new one.
              </div>
            )}
            {dupHits.length > 0 && (
              <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-300 rounded p-2 space-y-0.5" data-testid="duplicate-warning">
                <div className="font-semibold">⚠ Duplicate — this company is already on file:</div>
                {dupHits.slice(0, 4).map((d, i) => (
                  <div key={i}><span className="font-mono font-semibold">{d.sf}</span> · {d.name} · <b>{d.status}</b>{d.date ? ` · ${fmtUkDate(d.date)}` : ""}</div>
                ))}
                <div>The app is still created (broker tracking) but will be flagged <b>"Duplicate Application"</b> for UW and logged on {bestDup?.sf || "the existing deal"}. Use "Add to existing" instead if this is just an update.</div>
              </div>
            )}
            {(prefill?.email_matches || []).length > 0 && (
              <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-300 rounded p-2 space-y-0.5" data-testid="email-duplicate-warning">
                <div className="font-semibold">⚠ Same customer email on a recent application:</div>
                {(prefill.email_matches || []).slice(0, 4).map((d, i) => (
                  <div key={i}><span className="font-mono font-semibold">{d.sf}</span> · {d.name} · <b>{d.status}</b>{d.date ? ` · ${fmtUkDate(d.date)}` : ""}{d.email ? ` · ${d.email}` : ""}</div>
                ))}
                <div>Likely the <b>same client</b> applying via a different entity or broker (e.g. trust vs company). Check before creating.</div>
              </div>
            )}
            <div>
              <label className="text-[11px] font-medium text-slate-600">AI summary / key information (posted to the Monday update)</label>
              <Textarea value={fields.key_information ?? ""} onChange={(e) => set("key_information", e.target.value)}
                rows={2} className="text-xs" data-testid="app-wizard-key-info" />
            </div>
            {(prefill?.attachments || []).length > 0 && (
              <div>
                <div className="text-[11px] font-medium text-slate-600 mb-1">Attach to Monday item ({attSel.size} selected)</div>
                <div className="border rounded divide-y max-h-32 overflow-y-auto">
                  {prefill.attachments.map(a => (
                    <label key={a.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={attSel.has(a.id)}
                        onChange={() => setAttSel(prev => { const n = new Set(prev); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })} />
                      <Paperclip className="w-3 h-3 text-slate-400" />
                      <span className={cn("truncate flex-1", (a.is_zip || a.duplicate_of) && "text-slate-400")}>{a.name}</span>
                      {(a.forwarded || a.from_forward) && <span className="shrink-0 rounded border border-sky-200 bg-sky-50 px-1 text-[10px] text-sky-700" title="An email the sender attached as an Outlook item — filed as a PDF (and its own files); its text joins the update" data-testid="att-forwarded-tag">{a.forwarded ? "forwarded email" : "inside forwarded email"}</span>}
                      <DuplicateTag att={a} />
                      <span className="text-[10px] text-muted-foreground">{fmtKb(a.size)}</span>
                      <AttachmentActionIcon messageId={email.id} att={a} onPreview={setPreviewAtt} />
                    </label>
                  ))}
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={postUpdate} onChange={(e) => setPostUpdate(e.target.checked)} data-testid="app-wizard-post-update" />
              Post the email body as an Update on the Monday item
            </label>
            <label className="flex items-center gap-2 text-xs cursor-pointer" title="Reply to the sender from the applications mailbox with the acknowledgement template (New App / Top Up). Skipped automatically for duplicates.">
              <input type="checkbox" checked={sendAck} onChange={(e) => setSendAck(e.target.checked)} data-testid="app-wizard-send-ack" />
              Send acknowledgement email to the sender
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={creating || loading || !!err || !(fields.company_name || "").trim()} data-testid="app-wizard-create-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            {creating ? "Creating…" : `Create SF-${sfNumber ?? "…"} on Monday`}
          </Button>
        </DialogFooter>
        {previewAtt && (
          <AttachmentPreviewDialog messageId={email.id} att={previewAtt} onClose={() => setPreviewAtt(null)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PostUpdateDialog({ email, onClose, onPosted }) {
  const [loading, setLoading] = useState(true);
  const [prep, setPrep] = useState(null);
  const [err, setErr] = useState(null);
  const [pickedItem, setPickedItem] = useState(null);
  const [body, setBody] = useState("");
  const [attSel, setAttSel] = useState(new Set());
  const [sfSearch, setSfSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [posting, setPosting] = useState(false);
  const [previewAtt, setPreviewAtt] = useState(null);
  const [setStatus, setSetStatus] = useState("");

  const load = async (sf = null) => {
    try {
      sf ? setSearching(true) : setLoading(true);
      const r = await appInboxPrepUpdate(email.id, sf);
      setPrep(prev => sf && prev ? { ...prev, matches: r.matches } : r);
      if (!sf) {
        setBody(r.latest_reply || "");
        setAttSel(new Set((r.attachments || []).filter(a => a.selected).map(a => a.id)));
        if (r.intent === "Cancellation/Withdrawn") {
          setSetStatus(["Offer Rejected - NB", "Offer Rejected - TU"].includes(r.suggested_status) ? r.suggested_status : "");
        } else if (r.intent === "Open Banking") {
          setSetStatus("");
        } else {
          // the portal's one reply rule: IR deal → UW - IR, conditional-offer deal → UW - Query, otherwise keep the status
          setSetStatus(r.rule_status || "");
        }
      }
      const live = (r.matches || []).filter(m => m.is_live);
      setPickedItem(r.matches?.length === 1 ? r.matches[0] : live.length === 1 ? live[0] : null);
      setErr(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setLoading(false); setSearching(false); }
  };

  useEffect(() => { load(); }, [email.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!pickedItem) { toast.error("Pick the application to post to"); return; }
    setPosting(true);
    try {
      const r = await appInboxPostUpdate(email.id, {
        item_id: pickedItem.item_id,
        sf: pickedItem.sf,
        body,
        attachment_ids: [...attSel],
        set_status: setStatus || null,
        suggested_status: prep?.suggested_status || "",
        status_reasoning: prep?.status_reasoning || "",
        reply_kind: prep?.reply_kind || "",
        query_summary: prep?.query_summary || "",
      });
      (r.warnings || []).forEach(w => toast.warning(w));
      if (r.condition_alert) toast.info("Offer condition received — alert emailed to Paul");
      toast.success(`Update posted to ${pickedItem.sf || "item"}${r.status_set ? ` · status → ${r.status_set}` : ""}${r.attachments_queued ? ` · ${r.attachments_queued} files uploading in background` : ""}`);
      onPosted();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setPosting(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="post-update-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <Send className="w-4 h-4 text-blue-600" />Post email to existing application
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Bot className="w-6 h-6 mx-auto mb-2 text-violet-600 animate-pulse" />
            Finding the application &amp; extracting the latest reply…
          </div>
        ) : err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="text-[11px] font-medium text-slate-600 mb-1">Application ({(prep?.matches || []).length} match{(prep?.matches || []).length === 1 ? "" : "es"})</div>
              {(prep?.matches || []).length === 0 && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-2 mb-1">
                  No matching application found — search by SF number below.
                </div>
              )}
              <div className="border rounded divide-y max-h-36 overflow-y-auto">
                {(prep?.matches || []).map((m, i) => (
                  <label key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                    <input type="radio" name="post-target" checked={pickedItem?.item_id === m.item_id}
                      onChange={() => setPickedItem(m)} data-testid={`post-target-${m.sf}`} />
                    <span className="font-mono font-semibold">{m.sf}</span>
                    <span className="truncate flex-1">{m.name}</span>
                    {m.is_live && <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-300">LIVE</Badge>}
                    <Badge variant="outline" className="text-[9px]">{m.status || "—"}</Badge>
                    <span className="text-[10px] text-muted-foreground shrink-0">{fmtUkDate(m.date)}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-1 mt-1.5">
                <Input value={sfSearch} onChange={(e) => setSfSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sfSearch.trim() && load(sfSearch.trim())}
                  placeholder="Search by SF number, e.g. SF-27512" className="h-7 text-xs" data-testid="post-sf-search" />
                <Button size="sm" variant="outline" className="h-7 px-2" disabled={searching || !sfSearch.trim()}
                  onClick={() => load(sfSearch.trim())} data-testid="post-sf-search-btn">
                  {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                </Button>
              </div>
            </div>
            {prep?.rule_status && (
              <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1.5" data-testid="status-rule-banner">
                Portal rule: a reply on a deal at <b>{prep.match_status}</b> goes to <b>{prep.rule_status}</b>{prep.rule_status === "UW - Query" ? " — a credit query for whoever is on shift (not Francis - Review)" : ""}. Pre-selected below; change it if this reply is different.
              </div>
            )}
            {prep?.suggested_status && (
              <div className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1.5 flex items-center gap-2" data-testid="status-suggestion-banner">
                <Bot className="w-3 h-3 shrink-0" />
                <span className="flex-1">
                  AI suggested next status: <b>{prep.suggested_status}</b>
                  {prep.status_reasoning ? ` — ${prep.status_reasoning}` : ""}
                </span>
                {setStatus !== prep.suggested_status && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 shrink-0"
                    onClick={() => setSetStatus(prep.suggested_status)} data-testid="use-suggested-status-btn">
                    Use suggested
                  </Button>
                )}
              </div>
            )}
            {prep?.reply_kind && (
              <div className={cn("text-[11px] rounded px-2 py-1 border flex items-center gap-2", prep.reply_kind === "credit_query" ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-slate-50 border-slate-200 text-slate-700")} data-testid="reply-kind-banner">
                <span className="font-medium shrink-0">AI read this reply as:</span>
                <span data-testid="reply-kind-value">{REPLY_KIND_LABEL[prep.reply_kind] || prep.reply_kind}{prep.query_summary ? ` — ${prep.query_summary}` : ""}</span>
                {prep.reply_kind === "credit_query" && prep.match_status && <span className="ml-auto shrink-0 text-[10px]">→ rule: UW - Query</span>}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-slate-600">Set status on post:</span>
              <StatusSelect value={setStatus} onChange={setSetStatus} testId="post-status-select" />
            </div>
            <div>
              <div className="text-[11px] font-medium text-slate-600 mb-1">Update text (latest reply extracted by Claude — edit as needed)</div>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} className="text-xs" data-testid="post-update-body" />
            </div>
            {(prep?.attachments || []).length > 0 && (
              <div>
                <div className="text-[11px] font-medium text-slate-600 mb-1">Attach files to the Monday item ({attSel.size} selected)</div>
                <div className="border rounded divide-y max-h-28 overflow-y-auto">
                  {prep.attachments.map(a => (
                    <label key={a.id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={attSel.has(a.id)}
                        onChange={() => setAttSel(prev => { const n = new Set(prev); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })} />
                      <Paperclip className="w-3 h-3 text-slate-400" />
                      <span className={cn("truncate flex-1", (a.is_zip || a.duplicate_of) && "text-slate-400")}>{a.name}</span>
                      {(a.forwarded || a.from_forward) && <span className="shrink-0 rounded border border-sky-200 bg-sky-50 px-1 text-[10px] text-sky-700" title="An email the sender attached as an Outlook item — filed as a PDF (and its own files); its text joins the update" data-testid="att-forwarded-tag">{a.forwarded ? "forwarded email" : "inside forwarded email"}</span>}
                      <DuplicateTag att={a} />
                      <span className="text-[10px] text-muted-foreground">{fmtKb(a.size)}</span>
                      <AttachmentActionIcon messageId={email.id} att={a} onPreview={setPreviewAtt} />
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={posting || loading || !!err || !pickedItem || !body.trim()} data-testid="post-update-submit">
            {posting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            {posting ? "Posting…" : `Post update${pickedItem ? ` to ${pickedItem.sf}` : ""}`}
          </Button>
        </DialogFooter>
        {previewAtt && (
          <AttachmentPreviewDialog messageId={email.id} att={previewAtt} onClose={() => setPreviewAtt(null)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PipelineActivity({ pipe }) {
  const active = pipe && (pipe.running || pipe.triage_running || pipe.autopilot_running);
  if (!active) return null;
  const pct = pipe.total ? Math.min(100, Math.round((pipe.index / pipe.total) * 100)) : null;
  return (
    <div className="flex items-center gap-2 border border-green-300 bg-green-50 rounded-md px-2.5 py-1 max-w-md min-w-[200px]" data-testid="autopilot-activity">
      <Loader2 className="w-3.5 h-3.5 animate-spin text-green-700 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-semibold text-green-800 truncate" data-testid="autopilot-activity-label">
          {pipe.phase || "Working"}{pipe.total ? ` · ${pipe.index}/${pipe.total}` : "…"}
          {pipe.current ? ` — ${pipe.current}` : ""}
        </div>
        {pct != null && (
          <div className="h-1 bg-green-200 rounded-full mt-0.5 overflow-hidden">
            <div className="h-full bg-green-600 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="autopilot-progress-bar" />
          </div>
        )}
      </div>
    </div>
  );
}

function WizField({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-[11px] font-medium text-slate-600">{label}</label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="h-8 text-xs" data-testid={testId} />
    </div>
  );
}

function LoanDiskCustomerPicker({ value, onChange, seedQuery }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const term = (seedQuery || "").trim();
    if (term.length < 3 || value) return;
    let alive = true;
    appInboxLoanDiskBorrowers(term).then(r => { if (alive) setSuggested(r.borrowers || []); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedQuery]);

  const search = (term) => {
    setQ(term);
    clearTimeout(timer.current);
    if (term.trim().length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    timer.current = setTimeout(async () => {
      try { const r = await appInboxLoanDiskBorrowers(term.trim()); setResults(r.borrowers || []); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  };

  if (value) {
    return (
      <div className="flex items-center gap-1.5 h-8 border rounded px-2 bg-emerald-50 border-emerald-300" data-testid="app-wizard-ld-picked">
        <span className="text-xs text-emerald-900 truncate flex-1">
          {value.business_name || value.name}
          {value.name && value.business_name ? ` · ${value.name}` : ""}
          <span className="text-emerald-600"> · borrower #{value.borrower_id}</span>
        </span>
        <button type="button" onClick={() => onChange(null)} className="text-emerald-500 hover:text-emerald-800" data-testid="app-wizard-ld-clear">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  const rows = q.trim().length >= 2 ? results : suggested;
  const isSuggestion = q.trim().length < 2 && suggested.length > 0;
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => { search(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search LoanDisk by company, name, email or phone…"
        className="h-8 text-xs" data-testid="app-wizard-ld-search" />
      {searching && <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-2 top-2.5 text-slate-400" />}
      {isSuggestion && !open && (
        <div className="text-[10px] text-emerald-700 mt-0.5" data-testid="app-wizard-ld-suggest-hint">
          ↻ {suggested.length} possible existing LoanDisk customer{suggested.length > 1 ? "s" : ""} — click the box to view
        </div>
      )}
      {open && rows.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 border rounded bg-white shadow-lg divide-y max-h-44 overflow-y-auto" data-testid="app-wizard-ld-options">
          {isSuggestion && (
            <div className="px-2 py-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50">Suggested — matches the company name</div>
          )}
          {rows.map(b => (
            <button key={b.borrower_id} type="button"
              onMouseDown={(ev) => { ev.preventDefault(); onChange(b); setQ(""); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50"
              data-testid={`app-wizard-ld-opt-${b.borrower_id}`}>
              <div className="font-medium truncate">{b.business_name || b.name || `Borrower #${b.borrower_id}`}</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {[b.name, b.email, b.mobile].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && !searching && q.trim().length >= 2 && rows.length === 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 border rounded bg-white shadow-lg px-2 py-1.5 text-[11px] text-muted-foreground">
          No LoanDisk customers match "{q.trim()}"
        </div>
      )}
    </div>
  );
}

function BrokerPicker({ value, onChange, autoMatched }) {
  const [brokers, setBrokers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { appInboxBrokers().then(r => setBrokers(r.brokers || [])).catch(() => {}); }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return brokers.filter(b =>
      (b.name || "").toLowerCase().includes(s)
      || (b.trading_name || "").toLowerCase().includes(s)
      || (b.email || "").includes(s)).slice(0, 8);
  }, [brokers, q]);

  if (value) {
    return (
      <div className="flex items-center gap-1.5 h-8 border rounded px-2 bg-purple-50 border-purple-200" data-testid="app-wizard-broker-picked">
        <span className="text-xs text-purple-900 truncate flex-1">{value.name}</span>
        {autoMatched && <Badge variant="outline" className="text-[9px] bg-white text-purple-700 border-purple-300">auto-matched from sender</Badge>}
        <button type="button" onClick={() => onChange(null)} className="text-purple-400 hover:text-purple-700" data-testid="app-wizard-broker-clear">
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="No broker matched — search the broker board (optional)"
        className="h-8 text-xs" data-testid="app-wizard-broker-search" />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 border rounded bg-white shadow-lg divide-y max-h-44 overflow-y-auto" data-testid="app-wizard-broker-options">
          {filtered.map(b => (
            <button key={b.item_id} type="button"
              onMouseDown={(ev) => { ev.preventDefault(); onChange({ item_id: b.item_id, name: b.name }); setQ(""); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50"
              data-testid={`app-wizard-broker-opt-${b.item_id}`}>
              <div className="font-medium truncate">{b.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">{b.email || b.trading_name || ""}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReplyDialog({ email, matchMode, me, region = "UK", api, fromLabel = "", onClose, onSent, initialReplyAll = false }) {
  const [body, setBody] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [alsoDone, setAlsoDone] = useState(true);
  const [sending, setSending] = useState(false);
  const [matchOn, setMatchOn] = useState(!!matchMode);
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [newStatus, setNewStatus] = useState("");    // Match & Reply — optional status change
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [loadingMatches, setLoadingMatches] = useState(!!matchMode);
  // Dean (12 Oct 2026): Reply all · Cc · attachments so the team never has to open Outlook
  const [replyAll, setReplyAll] = useState(!!initialReplyAll);
  const [cc, setCc] = useState([]);
  const [showCc, setShowCc] = useState(false);
  // Dean/John Garcia (16 Oct 2026): sender is often a noreply@/notifications@
  // address that bounces a reply — let ops redirect the primary recipient.
  const [toOverride, setToOverride] = useState(null); // null = use the default thread recipient(s)
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(null);
  const fileSource = useMemo(() => appFileSource(), []);
  const messageAttachments = useMemo(() => ({ messageId: email.id, list: () => appInboxGetAttachments(email.id) }), [email.id]);
  const addFiles = (list) => {
    const next = mergeItems(files, Array.from(list || []).map(uploadItem));
    if (!next) { toast.error("Attachments must total under 25 MB"); return; }
    setFiles(next);
  };
  const badCc = cc.find(a => a.valid === false);
  const badTo = (toOverride || []).find(a => a.valid === false);
  const isOwnEmailThread = fromLabel && (email.from_email || "").toLowerCase() === fromLabel.toLowerCase() && email.to_email;

  // Reset the picked status whenever the user re-picks a different match
  // (so the previously-chosen target status doesn't leak across deals).
  useEffect(() => { setNewStatus(""); }, [picked?.item_id]);

  useEffect(() => {
    if (!matchOn) return;
    let alive = true;
    (async () => {
      setLoadingMatches(true);
      try {
        const r = await appInboxHistory(email.id);
        if (!alive) return;
        const rec = (r.history || []).filter(h => h.item_id);
        setMatches(rec);
        const live = rec.filter(h => h.is_live);
        setPicked(live[0] || rec[0] || null);
      } catch { /* noop */ }
      finally { if (alive) setLoadingMatches(false); }
    })();
    return () => { alive = false; };
  }, [matchOn, email.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = async () => {
    if (!search.trim()) return;
    setSearching(true);
    try {
      const r = await appInboxSearchApps(search.trim());
      const rec = (r.matches || []).filter(m => m.item_id);
      setMatches(rec);
      if (rec.length === 1) setPicked(rec[0]);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSearching(false); }
  };

  const submit = async () => {
    if (matchOn && !picked) { toast.error("Pick the application to log the reply to (or switch off logging)"); return; }
    setSending(true);
    try {
      const { html: sendHtml, inline } = splitInlineImages(bodyHtml);
      const attachments = await buildAttachmentsPayload(files, api.uploadChunk, setProgress, inline);
      const r = await appInboxReply(email.id, {
        body,
        body_html: sendHtml,
        reply_all: replyAll,
        to: toOverride ? toOverride.map(t => t.email) : undefined,
        cc: cc.map(t => t.email),
        attachments,
        item_id: matchOn ? picked.item_id : "",
        sf: matchOn ? picked.sf : "",
        move_to_done: alsoDone,
        actor: me?.name || me?.email || "Ops",
        new_status: matchOn ? (newStatus || "") : "",
      });
      (r.warnings || []).forEach(w => toast.warning(w));
      const fileCount = (r.attachments || 0) - inline.length;
      toast.success(
        `Reply sent`
        + (inline.length ? ` with ${inline.length} screenshot${inline.length === 1 ? "" : "s"}` : "")
        + (fileCount > 0 ? ` ${inline.length ? "and" : "with"} ${fileCount} attachment${fileCount === 1 ? "" : "s"}` : "")
        + (r.logged_to ? ` · logged on ${r.logged_to}` : "")
        + (r.status_set ? ` · status → ${r.status_set}` : "")
        + (r.moved_to_done ? " · moved to Done" : "")
      );
      onSent(r.moved_to_done);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); setProgress(null); }
  };

  const tryClose = () => { if (sending) return; if ((body.trim() || hasInlineImage(bodyHtml) || files.length) && !window.confirm("Discard this reply? What you've typed will be lost.")) return; onClose(); };

  return (
    <Dialog open onOpenChange={(v) => !v && tryClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" aria-describedby={undefined} data-testid="reply-dialog"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}>
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            {replyAll ? <ReplyAll className="w-4 h-4 text-blue-600" /> : <ReplyIcon className="w-4 h-4 text-blue-600" />}
            {matchOn ? "Match & Reply" : replyAll ? "Reply all" : "Reply"}
            {fromLabel && <span className="text-[11px] font-normal text-muted-foreground">from {fromLabel}</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground border rounded px-2 py-1.5 bg-slate-50 flex items-start gap-3">
            <div className="min-w-0 flex-1">
              {isOwnEmailThread ? (
                <div className="truncate" data-testid="reply-to-line"><b>To:</b> {email.to_name || ""} &lt;{email.to_email}&gt;{email.to_count > 1 && <span className="text-slate-400"> + {email.to_count - 1} more</span>}<span className="text-slate-400"> · this was our email — the reply goes to the people we wrote to, not back to {fromLabel}</span></div>
              ) : toOverride === null ? (
                <div className="truncate" data-testid="reply-to-line">
                  <b>To:</b> {email.from_name || ""} &lt;{email.from_email || ""}&gt;{replyAll && <span className="text-slate-400"> + everyone on the thread</span>}
                  <button type="button" className="ml-2 text-blue-700 hover:underline" data-testid="reply-to-edit-btn"
                    onClick={() => setToOverride(email.from_email ? [{ email: email.from_email, valid: true }] : [])}>
                    change recipient
                  </button>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="text-[11px] font-bold shrink-0 pt-1.5">To</span>
                  <div className="flex-1">
                    <RecipientInput api={api} value={toOverride} onChange={setToOverride} testid="reply-to-input"
                      placeholder="Redirect this reply to — e.g. a real inbox instead of noreply@…" autoFocus />
                    <button type="button" className="text-[11px] text-slate-500 hover:underline mt-0.5" data-testid="reply-to-reset-btn"
                      onClick={() => setToOverride(null)}>
                      use the original sender instead
                    </button>
                  </div>
                </div>
              )}
              <div className="truncate"><b>Re:</b> {email.subject || "(no subject)"}</div>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] cursor-pointer shrink-0 pt-0.5">
              <input type="checkbox" checked={replyAll} onChange={(e) => setReplyAll(e.target.checked)} data-testid="reply-all-toggle" />
              <ReplyAll className="w-3 h-3" />Reply all
            </label>
            {!showCc && <button type="button" className="text-[11px] text-blue-700 hover:underline shrink-0 pt-0.5" onClick={() => setShowCc(true)} data-testid="reply-show-cc">+ Cc</button>}
          </div>
          {showCc && (
            <div className="flex items-start gap-2">
              <span className="text-xs w-8 text-muted-foreground pt-2">Cc</span>
              <RecipientInput api={api} value={cc} onChange={setCc} testid="reply-cc" placeholder="Type a broker, company, SF number or an email address" />
            </div>
          )}
          {badCc && <p className="text-[11px] text-rose-700" data-testid="reply-bad-cc">"{badCc.email}" doesn't look like an email address</p>}
          {toOverride !== null && (badTo || toOverride.length === 0) && (
            <p className="text-[11px] text-rose-700" data-testid="reply-bad-to">
              {badTo ? `"${badTo.email}" doesn't look like an email address` : "Add at least one recipient, or switch back to the original sender"}
            </p>
          )}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[11px] font-medium text-slate-600">Your reply</label>
              <EmailTemplatesMenu dept="applications" region={region} testId="reply-templates"
                context={{ first_name: firstNameOf(email.from_name), customer_name: picked?.name || "", sf_number: picked?.sf || "", my_name: me?.name || "" }}
                getCurrent={() => ({ body_html: bodyHtml, body_text: body, subject: "" })}
                onInsert={({ body_html }) => setBodyHtml((cur) => insertTemplateHtml(cur, body_html))} />
            </div>
            <RichTextEditor value={bodyHtml} onChange={({ html, text }) => { setBodyHtml(html); setBody(text); }} minHeight={180} autoFocus testId="reply-body" images
              placeholder="Type your reply — it's sent from the applications mailbox on the same thread. Paste screenshots straight in (Ctrl+V)" />
          </div>
          <AttachmentsBox items={files} onChange={setFiles} disabled={sending} progress={progress} fileSource={fileSource} defaultScope={matchOn ? appScope(picked) : null} messageAttachments={messageAttachments} testPrefix="reply" />
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={matchOn} onChange={(e) => setMatchOn(e.target.checked)} data-testid="reply-match-toggle" />
            Log this reply as an update on the application file
          </label>
          {matchOn && (
            <div className="border rounded p-2 bg-blue-50/40 space-y-1.5" data-testid="reply-match-section">
              {loadingMatches ? (
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 py-1">
                  <Loader2 className="w-3 h-3 animate-spin" />Finding the application…
                </div>
              ) : (
                <>
                  {matches.length === 0 && (
                    <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded p-1.5">
                      No matching application — search by SF number or company below.
                    </div>
                  )}
                  {matches.length > 0 && (
                    <div className="border rounded divide-y max-h-32 overflow-y-auto bg-white">
                      {matches.slice(0, 8).map((m, i) => (
                        <label key={i} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
                          <input type="radio" name="reply-target" checked={picked?.item_id === m.item_id}
                            onChange={() => setPicked(m)} data-testid={`reply-target-${m.sf}`} />
                          <span className="font-mono font-semibold">{m.sf}</span>
                          <span className="truncate flex-1">{m.name}</span>
                          {m.is_live && <Badge variant="outline" className="text-[9px] bg-green-50 text-green-700 border-green-300">LIVE</Badge>}
                          <Badge variant="outline" className="text-[9px]">{m.status || "—"}</Badge>
                        </label>
                      ))}
                    </div>
                  )}
                  {picked && (
                    <div className="flex items-center gap-2 pt-1" data-testid="reply-status-change-row">
                      <span className="text-[11px] font-medium text-slate-600 shrink-0">Change status to:</span>
                      <StatusSelect value={newStatus} onChange={setNewStatus} testId="reply-new-status" />
                      {picked.status === "Funded" && newStatus && (
                        <span className="text-[10px] text-amber-700">
                          Funded is protected — status change will be ignored
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-1">
                    <Input value={search} onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doSearch()}
                      placeholder="Search SF-27512 or company name" className="h-7 text-xs bg-white" data-testid="reply-sf-search" />
                    <Button size="sm" variant="outline" className="h-7 px-2" disabled={searching || !search.trim()}
                      onClick={doSearch} data-testid="reply-sf-search-btn">
                      {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={alsoDone} onChange={(e) => setAlsoDone(e.target.checked)} data-testid="reply-move-done" />
            Move the email to Done after sending
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={tryClose} disabled={sending}>Cancel</Button>
          <Button onClick={submit} disabled={sending || (!body.trim() && !hasInlineImage(bodyHtml)) || !!badCc || (toOverride !== null && (!!badTo || toOverride.length === 0)) || (matchOn && loadingMatches)} data-testid="reply-submit">
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ReplyIcon className="w-4 h-4 mr-1" />}
            {sending
              ? (progress ? "Uploading…" : "Sending…")
              : matchOn && picked
                ? `Send & log on ${picked.sf}${newStatus ? ` → ${newStatus}` : ""}`
                : "Send reply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
